import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import useApi from '../../hooks/useApi';
import SmtpTestResultsDialog from '../smtpSettings/dialogs/SmtpTestResultsDialog';
import Card from '@mui/material/Card';
import AlertBar from '../../alertBar/AlertBar';
import CardContent from '@mui/material/CardContent';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';
import config from '../../config';

const cellStyles = {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
};

const SmtpSettings = () => {
    const { data: account } = useSuspenseFetch('account');
    const [smtpUsername, setSmtpUsername] = useState(account.smtpUsername);
    const [smtpPassword, setSmtpPassword] = useState('');
    const showSmtpPassword = Boolean(account.hasSmtpPassword);
    const [smtpTenant, setSmtpTenant] = useState(account.smtpTenant);
    const [isSmtpTestSubmitting, setIsSmtpTestSubmitting] = useState(false);
    const [smtpTestResult, setSmtpTestResult] = useState();
    const [smtpTestResultDialogIsOpen, setSmtpTestResultDialogIsOpen] = useState(false);
    const { handlePost } = useApi();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const isInternalComms = account?.licence?.isInternalComms ?? false;
    const putMutation = usePutMutation('account');

    const clientId = config.TRUSTED_DELIVERY_CLIENT_ID;
    const scope = config.TRUSTED_DELIVERY_SCOPE;
    const redirectUri = `${window.location.origin}/window_redirect.html`;

    const onTestSmtpSettings = async () => {
        setIsSmtpTestSubmitting(true);

        const response = await handlePost('account/test-smtp-settings', { smtpUsername, smtpTenant });

        if (!response.ok) {
            return;
        }

        const data = await response.json();

        setIsSmtpTestSubmitting(false);
        setSmtpTestResult(data);
        setSmtpTestResultDialogIsOpen(true);
    };

    const handleAdminConsent = async () => {
        const url = `https://login.microsoftonline.com/${smtpTenant}/v2.0/adminconsent
            ?client_id=${clientId}
            &redirect_uri=${encodeURIComponent(redirectUri)}
            &scope=${scope}`;

        window.open(url, 'adminConsent', 'width=600,height=600');
    };

    const onSubmit = async e => {
        e.preventDefault();
        setSmtpTestResultDialogIsOpen(false);
        putMutation.mutate({ smtpUsername, smtpPassword, smtpTenant }, { onSuccess: () => setSmtpPassword('') });
    };

    useEffect(() => {
        setHasUnsavedChanges(smtpUsername && smtpUsername !== account.smtpUsername
            || smtpPassword && smtpPassword !== ''
            || smtpTenant && smtpTenant !== account.smtpTenant
        );
    }, [account, smtpUsername, smtpPassword, smtpTenant]);

    return (
        <>
            <AlertBar
                shown={putMutation.isPending || hasUnsavedChanges}
                action={(
                    <LoadingButton
                        variant="outlined"
                        size="small"
                        loading={putMutation.isPending}
                        onClick={onSubmit}
                    >
                        Save Changes
                    </LoadingButton>
                )}
                positionTop={128}
            >
                {putMutation.isPending ? 'Updating...' : 'You have unsaved changes.'}
            </AlertBar>
            <Card>
                <CardContent sx={{ p: 0 }}>
                    <Toolbar variant="dense" disableGutters sx={{ px: 2, display: 'flex' }}>
                        <Typography component="h3" variant="h6" sx={{ flexGrow: 1 }}>SMTP</Typography>
                        <Button onClick={onTestSmtpSettings}>
                            {isSmtpTestSubmitting ? 'Testing' : 'Test'}
                        </Button>
                    </Toolbar>
                    <Divider />
                    <Box sx={{ px: 2 }}>
                        <form onSubmit={onSubmit} noValidate autoComplete="off">
                            <TextField value={account.smtpHost} type="text" label="Host" disabled={true} fullWidth={true} />
                            <TextField value={account.smtpPort} type="text" label="Port" disabled={true} fullWidth={true} />
                            <TextField value={smtpUsername} onChange={e => setSmtpUsername(e.target.value)} required type="email" label="Username" fullWidth={true} />
                            {showSmtpPassword && (
                                <TextField value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} required type="text" label="Change password" fullWidth={true} />
                            )}
                            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                                <Alert severity="info">
                                    Request consent from an administrator of your AzureAD tenant to send emails using NewZapp Trusted Delivery.
                                </Alert>
                                <TextField value={smtpTenant} onChange={e => setSmtpTenant(e.target.value)} required type="text" label="Tenant" fullWidth={true} />
                                <Button component="a" variant="outlined" endIcon={<DownloadIcon />} href="./docs/NewZapp_Trusted_Delivery_OAuth_Setup.pdf" target="_blank" sx={{ mr: 1 }}>
                                    Implementation Guide (PDF)
                                </Button>
                                <Button onClick={handleAdminConsent} disabled={!Boolean(smtpTenant)}>Request Admin Consent</Button>
                            </Paper>
                        </form>
                    </Box>
                </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
                <Toolbar variant="dense" disableGutters sx={{ px: 2 }}>
                    <div style={{ flexGrow: 1 }}>
                        <Typography component="h3" variant="h6">Safe Senders</Typography>
                    </div>
                </Toolbar>
                <Divider />
                {account?.safeSenders.length > 0 ? (
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {account.safeSenders.map(emailAddress => (
                                    <TableRow key={emailAddress}>
                                        <TableCell sx={cellStyles}>{emailAddress}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box sx={{ p: 2 }}>
                        <Typography variant="body2">This account has no safe senders set up.</Typography>
                    </Box>
                )}
            </Card>

            {(isInternalComms && account?.allowedRecipients?.length > 0) && (
                <Paper sx={{ mt: 2 }}>
                    <Box>
                        <Typography sx={{ mx: 2, pt: 1 }} component="h3" variant="h6" gutterBottom>Allowed Recipients</Typography>
                        <Divider />
                        <Box sx={{ p: 1 }}>
                            {account.allowedRecipients.map(recipient => <Chip key={recipient.id} label={recipient.pattern} sx={{ mr: 1 }} />)}
                        </Box>
                    </Box>
                </Paper>
            )}
            {smtpTestResult && (
                <SmtpTestResultsDialog
                    open={smtpTestResultDialogIsOpen}
                    onClose={() => setSmtpTestResultDialogIsOpen(false)}
                    succeeded={smtpTestResult.succeeded}
                    message={smtpTestResult.message}
                />
            )}
            {(putMutation.isPending || isSmtpTestSubmitting) && (
                <LoadingOverlay />
            )}
        </>
    );
};

export default SmtpSettings;